import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cpu, CheckCircle2, AlertCircle, RefreshCw, ArrowRight, Zap, Lock } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useDiagnosticContext } from '../../../context/DiagnosticContext';
import { useHistory } from '../../../context/HistoryContext';
import { generateReport } from '../../../services/reportEngine';
import { deductReportCredit, stashDevCreditNotice } from '../../../services/useCredit';
import { PCCUtils } from '../../../utils/PCCUtils';
import '../DiagnosticPremium.css';

const ANALYSIS_STAGES = [
  'Decoding diagnostic data',
  'Verifying file signature',
  'Identifying system components',
  'Running health checks',
  'AI Root Cause Analysis',
  'Finalizing report'
];

export function ReportView() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const { saveReport } = useHistory();
  const {
    uploadedFile, setAnalysisResult, setAnalysisStatus,
    analysisStatus, analysisResult, decodedPccData, setDecodedPccData
  } = useDiagnosticContext();

  const [currentStage, setCurrentStage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    if (analysisStatus === 'idle') {
      runAnalysis();
    }
  }, [analysisStatus]);

  const runAnalysis = async () => {
    if (!uploadedFile) {
      setError('No diagnostic file found. Please go back and upload your .pcc file.');
      return;
    }

    setIsProcessing(true);
    setAnalysisStatus('running');
    setCurrentStage(0);

    try {
      for (let i = 0; i < ANALYSIS_STAGES.length - 1; i++) {
        setCurrentStage(i);
        await new Promise(r => setTimeout(r, 600));
      }

      const buffer = await uploadedFile.arrayBuffer();
      const decoded = await PCCUtils.decode(buffer);
      setDecodedPccData(decoded);

      const result = await generateReport(decoded, 'free');

      setCurrentStage(ANALYSIS_STAGES.length - 1);
      await new Promise(r => setTimeout(r, 400));

      setAnalysisResult(result);
      setAnalysisStatus('success');
    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(err.message || 'Analysis failed.');
      setAnalysisStatus('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewFreeReport = () => {
    if (!analysisResult) return;
    saveReport({
      id: `FREE-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
      timestamp: new Date().toISOString(),
      category: 'Free Diagnostic Preview',
      score: analysisResult.healthScore,
      findingsCount: analysisResult.criticalCount + analysisResult.warningCount,
      severity: analysisResult.healthScore < 60 ? 'high' : analysisResult.healthScore < 85 ? 'medium' : 'low',
      manufacturer: analysisResult.manufacturer || analysisResult.overview?.manufacturer || 'Unknown',
      reportData: analysisResult,
      isPro: false,
      reportType: 'FREE_PREVIEW',
    });
    navigate('/report');
  };

  const handleUpgrade = () => navigate('/pricing');

  const handleUseCredit = async () => {
    if (!user || !decodedPccData) {
      setModalError('Missing session or scan data. Please restart the diagnostic.');
      return;
    }

    setIsProcessing(true);
    setModalError(null);
    try {
      const isProUser = profile?.isPro || profile?.creditsMode === 'unlimited';

      if (!isProUser) {
        const idToken = await user.getIdToken();
        const creditResult = await deductReportCredit(idToken, {
          currentCredits: profile?.credits,
        });
        if (creditResult.devMode && creditResult.devMessage) {
          stashDevCreditNotice(creditResult.devMessage);
        } else {
          await refreshProfile();
        }
      }

      // Re-run the engine in PRO mode so we get the full 80-check report,
      // not the free-trimmed one.
      const proResult = await generateReport(decodedPccData, 'pro');

      saveReport({
        id: `PRO-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
        timestamp: new Date().toISOString(),
        category: 'Full Pro Diagnostic',
        score: proResult.healthScore,
        findingsCount: proResult.criticalCount + proResult.warningCount,
        severity: proResult.healthScore < 60 ? 'high' : proResult.healthScore < 85 ? 'medium' : 'low',
        manufacturer: proResult.manufacturer || proResult.overview?.manufacturer || 'Unknown',
        reportData: proResult,
        isPro: true,
        reportType: 'PRO_REPORT',
      });

      setShowConfirmModal(false);
      navigate('/report');
    } catch (err: any) {
      console.error('Pro report generation failed:', err);
      setModalError(err.message || 'Failed to process credit. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (analysisStatus === 'running') {
    return (
      <div className="stepContainer fade-up">
        <div className="premiumCardWrapper">
          <div className="premiumCardGlow"></div>
          <div className="premiumCardInner">
            <div className="analysisContainer">
              <div className="iconBox">
                <Cpu size={32} color="var(--accent)" className="spin" style={{ animation: 'pulse 2s infinite' }} />
              </div>
              <h1 style={{ fontSize: '24px', fontWeight: 900, marginBottom: '8px' }}>Analyzing Your PC</h1>
              <p style={{ color: '#9999a8', marginBottom: '24px' }}>Decoding your diagnostic capture and running checks.</p>

              <div className="analysisChecklist">
                {ANALYSIS_STAGES.map((stage, i) => (
                  <div
                    key={i}
                    className={`analysisCheckItem ${currentStage === i ? 'active' : currentStage > i ? 'done' : ''}`}
                  >
                    <CheckCircle2 size={18} className="checkIcon" />
                    <span>{stage}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (analysisStatus === 'error') {
    return (
      <div className="stepContainer fade-up">
        <div className="premiumCardWrapper">
          <div className="premiumCardGlow"></div>
          <div className="premiumCardInner" style={{ padding: '60px 40px' }}>
            <AlertCircle size={64} style={{ color: 'var(--red)', marginBottom: '24px' }} />
            <h1 style={{ fontSize: '24px', fontWeight: 900, marginBottom: '16px' }}>Analysis Failed</h1>
            <p style={{ color: '#9999a8', marginBottom: '32px' }}>{error}</p>
            <button className="btnPrimary" onClick={runAnalysis} style={{ margin: '0 auto' }}>
              Retry Analysis <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (analysisStatus === 'success' && analysisResult) {
    const ov = analysisResult.overview || {};
    return (
      <div className="stepContainer fade-up">
        <div className="premiumCardWrapper">
          <div className="premiumCardGlow"></div>
          <div className="premiumCardInner">
            <h1 style={{ fontSize: '28px', fontWeight: 900, marginBottom: '16px' }}>Scan Decoded</h1>

            <div className="scorePreviewCard">
              <div className="scoreCircleLarge">{analysisResult.healthScore}</div>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#eaeaf0' }}>
                6 free checks analysed · 74 advanced checks locked
              </h2>
              <p style={{ color: '#9999a8', fontSize: '14px', maxWidth: '520px', margin: '14px auto', lineHeight: 1.6 }}>
                {ov.manufacturer} {ov.model} · {ov.os} · {ov.ramGB} GB RAM · C: {ov.diskFreeGB} GB free of {ov.diskTotalGB} GB
              </p>
            </div>

            <div className="ctaActions" style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
              {(profile?.credits || 0) > 0 || profile?.isPro ? (
                <button className="btnPrimary" onClick={() => setShowConfirmModal(true)}>
                  Unlock Full Pro Report (1 Credit) <ArrowRight size={18} />
                </button>
              ) : (
                <button className="btnPrimary" onClick={handleUpgrade}>
                  Upgrade to Pro to See Full Report <Zap size={18} />
                </button>
              )}

              <button
                className="btnSecondary"
                style={{ border: 'none' }}
                onClick={handleViewFreeReport}
              >
                <Lock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                View Free Preview (6 checks) &rarr;
              </button>
            </div>
          </div>
        </div>

        {showConfirmModal && (
          <div className="modalOverlay">
            <div className="modalContent">
              <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '12px' }}>Generate Pro Report?</h2>
              <p style={{ color: '#9999a8', fontSize: '14px', marginBottom: '16px' }}>
                This will use 1 credit. You have {profile?.credits || 0} credits remaining.
              </p>
              {import.meta.env.DEV && (
                <p style={{ color: '#6b8cae', fontSize: '12px', marginBottom: '16px', lineHeight: 1.5 }}>
                  Local dev: if the credit API is unavailable, a test unlock runs without deducting Firestore credits.
                </p>
              )}
              {modalError && (
                <div style={{ background: 'rgba(255,68,68,0.12)', border: '1px solid rgba(255,68,68,0.3)', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#ff6b6b', fontSize: '13px', lineHeight: 1.5 }}>
                  {modalError}
                </div>
              )}
              <div className="modalActions">
                <button className="btnPrimary" onClick={handleUseCredit} disabled={isProcessing}>
                  {isProcessing ? 'Generating Pro report…' : modalError ? 'Retry' : 'Confirm'}
                </button>
                <button className="btnSecondary" onClick={() => { setShowConfirmModal(false); setModalError(null); }}>
                  {modalError ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
